import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../api';
import { CATALOG_KEY, type Kind } from './ItemForm';

/** An item the server found for the typed name (only what the person may see of it). */
export type SimilarItem = { id: string; name: string; kind?: Kind; version: number };
export type HintAction = { label: string; onClick: () => void };

/** The same name without case, spaces, hyphens, dots, Polish letters (a mark between digits stays): what the server compares too (P14). */
export const nameKey = (name: string): string =>
  name
    .normalize('NFD')
    .replace(/\p{M}+/gu, '')
    .toLowerCase()
    .replace(/ł/g, 'l')
    .replace(/(\p{N})[^\p{L}\p{N}]+(?=\p{N})/gu, '$1_') // "1,5" stays apart from "15"
    .replace(/[^\p{L}\p{N}_]+/gu, '');

/** How long the name must stay unchanged before the server is asked. */
const DEBOUNCE_MS = 300;

/**
 * Under the name field while typing (P14, owner 22:59): "Taka pozycja już jest: X" with what to do about it, or the
 * softer "Podobna: X" when one name holds the whole other. Never blocks saving (owner: no "are you sure" dialogs);
 * `dismissLabel` ("Utwórz mimo to") hides the warning for this name.
 */
export function DuplicateNameHint({
  name,
  excludeId,
  actions,
  dismissLabel = 'Utwórz mimo to',
}: {
  name: string;
  excludeId?: string | undefined;
  actions: (item: SimilarItem) => HintAction[];
  dismissLabel?: string;
}) {
  const [typed, setTyped] = useState(name.trim());
  useEffect(() => {
    const t = setTimeout(() => setTyped(name.trim()), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [name]);
  const [dismissed, setDismissed] = useState<string | null>(null);
  const key = nameKey(typed);
  const query = useQuery({
    queryKey: [...CATALOG_KEY, 'similar', key, excludeId ?? ''],
    enabled: key.length >= 2,
    staleTime: 10_000,
    queryFn: () => api<{ same: SimilarItem[]; possible: SimilarItem[] }>(`/api/catalog/items/similar?${new URLSearchParams({ name: typed, ...(excludeId ? { excludeId } : {}) }).toString()}`),
  });
  // the answer for an older name says nothing about the current one
  if (key.length < 2 || !query.data || dismissed === key || nameKey(name) !== key) return null;
  const same = query.data.same ?? [];
  const possible = query.data.possible ?? [];
  if (same.length > 0) {
    const first = same[0]!;
    return (
      <div className="dup-hint" role="status" data-feature-id="catalog.duplicates">
        <span>
          Taka pozycja już jest: <strong>{first.name}</strong>
          {same.length > 1 && <span className="muted"> (i {same.length - 1} więcej)</span>}
        </span>
        <span className="dup-hint-actions">
          {actions(first).map((a) => (
            <button key={a.label} type="button" className="btn small ghost" onClick={a.onClick}>
              {a.label}
            </button>
          ))}
          <button type="button" className="btn small ghost" onClick={() => setDismissed(key)}>
            {dismissLabel}
          </button>
        </span>
      </div>
    );
  }
  if (possible.length > 0) {
    return (
      <p className="dup-hint soft hint" role="status" data-feature-id="catalog.duplicates">
        Podobna: {possible.map((p) => p.name).join(', ')}
      </p>
    );
  }
  return null;
}
